import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { debtorFullName, filingDebtors } from "../utils/debtors";

const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const MARGIN = 72;
const CONTENT_WIDTH = PAGE_WIDTH - (MARGIN * 2);
const BLACK = rgb(0, 0, 0);
const LIGHT_GRAY = rgb(0.92, 0.92, 0.92);
const BODY_SIZE = 11;

const numberValue = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const cleanText = (value) => String(value ?? "")
  .replace(/[\u2018\u2019]/g, "'")
  .replace(/[\u201c\u201d]/g, '"')
  .replace(/[\u2013\u2014]/g, "-")
  .replace(/\u00a0/g, " ");

const safeLine = (...parts) => parts
  .filter((part) => part !== null && part !== undefined && String(part).trim())
  .map((part) => String(part).trim())
  .join(" ");

export function formatLienCurrency(value) {
  return numberValue(value).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function calculateLienAvoidance({ lienAmount, otherLiensTotal, exemptionAmount, propertyValue }) {
  const lien = Math.max(0, numberValue(lienAmount));
  const otherLiens = Math.max(0, numberValue(otherLiensTotal));
  const exemption = Math.max(0, numberValue(exemptionAmount));
  const value = Math.max(0, numberValue(propertyValue));
  const totalLiensAndExemption = lien + otherLiens + exemption;
  const impairment = Math.max(0, totalLiensAndExemption - value);
  const avoidableAmount = Math.min(lien, impairment);
  const remainingSecuredAmount = Math.max(0, lien - avoidableAmount);

  return {
    lienAmount: lien,
    otherLiensTotal: otherLiens,
    exemptionAmount: exemption,
    propertyValue: value,
    totalLiensAndExemption,
    impairment,
    avoidableAmount,
    remainingSecuredAmount,
    fullyAvoided: lien > 0 && avoidableAmount >= lien - 0.005,
  };
}

function formatLongDate(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function filingDebtorNames(caseData) {
  return filingDebtors(caseData?.debtors)
    .map(debtorFullName)
    .filter(Boolean)
    .slice(0, 2);
}

function attorneyFullName(attorney) {
  return safeLine(attorney?.first_name, attorney?.second_name, attorney?.last_name);
}

function attorneyAddress(attorney) {
  return [
    safeLine(attorney?.street),
    safeLine(attorney?.street2),
    safeLine(
      attorney?.city ? `${attorney.city},` : "",
      attorney?.state,
      attorney?.zip_code,
    ),
  ].filter(Boolean);
}

function attorneyInlineAddress(attorney) {
  return attorneyAddress(attorney).join(", ");
}

function stripRecordingLabel(value, label) {
  return cleanText(value)
    .trim()
    .replace(new RegExp(`^${label}\\s*[:#-]?\\s*`, "i"), "")
    .trim();
}

function recordingReference(book, page) {
  return `Book ${stripRecordingLabel(book, "book")}, Page ${stripRecordingLabel(page, "page")}`;
}

function formatLienDate(value) {
  if (!value) return "Not entered";
  const date = /^\d{4}-\d{2}-\d{2}$/.test(String(value))
    ? new Date(`${value}T12:00:00`)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return cleanText(value);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function courtHeading(attorney) {
  const district = cleanText(attorney?.filing_district || "Eastern District of Tennessee").trim();
  const rawDivision = cleanText(attorney?.division || "Northeastern").trim();
  const division = /division$/i.test(rawDivision) ? rawDivision : `${rawDivision} Division`;
  return ["United States Bankruptcy Court", district, division];
}

function wrapText(font, text, size, maxWidth) {
  const paragraphs = cleanText(text).split(/\r?\n/);
  const lines = [];

  paragraphs.forEach((paragraph, paragraphIndex) => {
    const words = paragraph.trim().split(/\s+/).filter(Boolean);
    if (!words.length) {
      lines.push("");
      return;
    }

    let line = "";
    words.forEach((word) => {
      const candidate = line ? `${line} ${word}` : word;
      if (!line || font.widthOfTextAtSize(candidate, size) <= maxWidth) {
        line = candidate;
      } else {
        lines.push(line);
        line = word;
      }
    });
    if (line) lines.push(line);
    if (paragraphIndex < paragraphs.length - 1 && paragraph.trim()) lines.push("");
  });

  return lines;
}

function drawCentered(page, text, font, size, y) {
  const cleaned = cleanText(text);
  const width = font.widthOfTextAtSize(cleaned, size);
  page.drawText(cleaned, {
    x: (PAGE_WIDTH - width) / 2,
    y,
    size,
    font,
    color: BLACK,
  });
}

function drawFixedWrappedText(page, text, {
  x,
  y,
  maxWidth,
  font,
  size = 11,
  lineHeight = 13,
  maxLines = 4,
}) {
  const lines = wrapText(font, text, size, maxWidth).slice(0, maxLines);
  lines.forEach((line, index) => {
    page.drawText(line, {
      x,
      y: y - (index * lineHeight),
      size,
      font,
      color: BLACK,
    });
  });
  return y - (lines.length * lineHeight);
}

function drawRichWrappedText(page, segments, {
  x,
  y,
  width,
  regular,
  bold,
  size,
  lineHeight,
}) {
  const lines = [[]];
  let lineWidth = 0;

  segments.forEach((segment) => {
    const font = segment.bold ? bold : regular;
    cleanText(segment.text).trim().split(/\s+/).filter(Boolean).forEach((word) => {
      const wordWidth = font.widthOfTextAtSize(word, size);
      const spaceWidth = lines[lines.length - 1].length
        ? font.widthOfTextAtSize(" ", size)
        : 0;
      if (lines[lines.length - 1].length && lineWidth + spaceWidth + wordWidth > width) {
        lines.push([]);
        lineWidth = 0;
      }
      const leadingSpace = lines[lines.length - 1].length
        ? font.widthOfTextAtSize(" ", size)
        : 0;
      lines[lines.length - 1].push({
        word,
        font,
        underline: Boolean(segment.underline),
        leadingSpace,
        width: wordWidth,
      });
      lineWidth += leadingSpace + wordWidth;
    });
  });

  lines.forEach((line, lineIndex) => {
    let currentX = x;
    const currentY = y - (lineIndex * lineHeight);
    line.forEach((token) => {
      currentX += token.leadingSpace;
      page.drawText(token.word, {
        x: currentX,
        y: currentY,
        size,
        font: token.font,
        color: BLACK,
      });
      if (token.underline) {
        page.drawLine({
          start: { x: currentX, y: currentY - 1.6 },
          end: { x: currentX + token.width, y: currentY - 1.6 },
          thickness: 0.7,
          color: BLACK,
        });
      }
      currentX += token.width;
    });
  });

  return y - (lines.length * lineHeight);
}

function drawCourtHeading(page, attorney, bold, topY) {
  const lines = courtHeading(attorney);
  drawCentered(page, lines[0], bold, 15, topY);
  drawCentered(page, lines[1], bold, 15, topY - 19);
  drawCentered(page, lines[2], bold, 15, topY - 38);
}

function drawCaption(page, { names, caseNumber, chapter, regular, bold, topY }) {
  const bottomY = topY - 70;
  const dividerX = 306;
  page.drawLine({
    start: { x: dividerX, y: bottomY },
    end: { x: dividerX, y: topY },
    thickness: 0.8,
    color: BLACK,
  });

  page.drawText("In re", { x: MARGIN, y: topY - 14, size: 11, font: regular, color: BLACK });
  let debtorNameY = topY - 14;
  (names.length ? names : ["Debtor"]).forEach((name) => {
    debtorNameY = drawFixedWrappedText(page, name, {
      x: 140,
      y: debtorNameY,
      maxWidth: 154,
      font: regular,
      size: 10.5,
      lineHeight: 13,
      maxLines: 2,
    });
  });
  page.drawText("Debtor(s)", { x: 183, y: bottomY + 4, size: 10.5, font: regular, color: BLACK });

  page.drawText("Case No.", { x: 330, y: topY - 14, size: 11, font: regular, color: BLACK });
  drawFixedWrappedText(page, String(caseNumber || ""), {
    x: 420,
    y: topY - 14,
    maxWidth: 120,
    font: regular,
    size: 11,
    lineHeight: 13,
    maxLines: 2,
  });
  page.drawText("Chapter", { x: 330, y: topY - 48, size: 11, font: regular, color: BLACK });
  page.drawText(String(chapter || ""), { x: 420, y: topY - 48, size: 11, font: regular, color: BLACK });
}

function drawSignatureBlock(page, {
  attorney,
  x,
  y,
  regular,
  bold,
  size = 10.5,
  lineHeight = 13,
}) {
  const name = attorneyFullName(attorney) || "Attorney";
  page.drawText(`/s/ ${cleanText(name)}`, { x, y, size, font: bold, color: BLACK });
  page.drawLine({
    start: { x, y: y - 3 },
    end: { x: x + 190, y: y - 3 },
    thickness: 0.7,
    color: BLACK,
  });

  const lines = [
    "Signature of Attorney",
    safeLine(name, attorney?.bar_number),
    "Cisowski Law",
    ...attorneyAddress(attorney),
    attorney?.phone_number || "",
    attorney?.email || "",
  ].filter(Boolean);
  lines.forEach((line, index) => {
    page.drawText(cleanText(line), {
      x,
      y: y - size - 6 - (index * lineHeight),
      size,
      font: regular,
      color: BLACK,
    });
  });
}

function drawNotice(page, { attorney, regular, bold, topY }) {
  const x = 31;
  const width = PAGE_WIDTH - 62;
  const innerX = x + 9;
  const innerWidth = width - 18;
  drawCentered(page, "NOTICE OF OPPORTUNITY TO OBJECT AND FOR HEARING", bold, 11, topY - 18);
  page.drawText("Notice is hereby given that:", {
    x: innerX,
    y: topY - 48,
    size: 10.5,
    font: bold,
    color: BLACK,
  });

  const attorneyName = attorneyFullName(attorney) || "the movant's attorney";
  const address = attorneyInlineAddress(attorney);
  let y = drawRichWrappedText(page, [
    {
      text: "Pursuant to E.D. Tenn. LBR 9013-1(h), the court may consider this matter without further notice or hearing unless a party in interest files an objection. If you object to the relief requested in this paper, you must file with the clerk of court at",
      bold: true,
    },
    {
      text: "United States Bankruptcy Court, James H. Quillen U.S. Courthouse, 220 West Depot Street, Ste 218, Greeneville, TN 37743,",
      bold: true,
      underline: true,
    },
    {
      text: `an objection within 21 days from the date this paper was filed and serve a copy on the movant's attorney, ${attorneyName}${address ? ` at ${address}` : ""}. If you file and serve an objection within the time permitted, the court will schedule a hearing and you will be notified. If you do not file an objection within the time permitted, the court will consider that you do not oppose the granting of the relief requested in this paper and may grant the relief requested without further notice or hearing.`,
    },
  ], {
    x: innerX,
    y: topY - 76,
    width: innerWidth,
    regular,
    bold,
    size: 10.5,
    lineHeight: 13.2,
  });
  y -= 10;
  y = drawRichWrappedText(page, [
    { text: "Your rights may be affected.", bold: true, underline: true },
    {
      text: "You should read these papers carefully and discuss them with your attorney, if you have one in this bankruptcy case. If you do not have an attorney, you may wish to consult one.",
      bold: true,
    },
  ], {
    x: innerX,
    y,
    width: innerWidth,
    regular,
    bold,
    size: 10.5,
    lineHeight: 13.2,
  });
  const bottomY = y - 9;
  page.drawRectangle({
    x,
    y: bottomY,
    width,
    height: topY - bottomY,
    borderWidth: 0.75,
    borderColor: BLACK,
  });
  return bottomY;
}

function tableRowHeight(row, columns, font, size, lineHeight, padding = 5) {
  return Math.max(
    24,
    ...row.map((cell, index) => (
      wrapText(font, cell, size, columns[index].width - (padding * 2)).length * lineHeight + (padding * 2)
    )),
  );
}

function drawTableRow(page, { row, columns, x, topY, height, font, size, lineHeight, fill }) {
  let currentX = x;
  columns.forEach((column, index) => {
    page.drawRectangle({
      x: currentX,
      y: topY - height,
      width: column.width,
      height,
      color: fill || undefined,
      borderColor: BLACK,
      borderWidth: 0.65,
    });
    const lines = wrapText(font, row[index], size, column.width - 10);
    lines.forEach((line, lineIndex) => {
      page.drawText(line, {
        x: currentX + 5,
        y: topY - 6 - size - (lineIndex * lineHeight),
        size,
        font,
        color: BLACK,
      });
    });
    currentX += column.width;
  });
  return topY - height;
}

function createFlowLayout({ pdfDoc, regular, bold, page, y, topY = 720, bottomY = MARGIN }) {
  let currentPage = page;
  let cursorY = y;

  const nextPage = () => {
    currentPage = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
    cursorY = topY;
  };

  const ensure = (height) => {
    if (cursorY - height < bottomY) nextPage();
  };

  const addSpace = (height) => {
    if (cursorY - height < bottomY) nextPage();
    else cursorY -= height;
  };

  const paragraph = (text, {
    x = MARGIN,
    width = CONTENT_WIDTH,
    font = regular,
    size = BODY_SIZE,
    lineHeight = 18,
    firstLineIndent = 0,
    after = 10,
  } = {}) => {
    const lines = wrapText(font, text, size, width - firstLineIndent);
    lines.forEach((line, index) => {
      ensure(lineHeight);
      currentPage.drawText(line, {
        x: x + (index === 0 ? firstLineIndent : 0),
        y: cursorY,
        size,
        font,
        color: BLACK,
      });
      cursorY -= lineHeight;
    });
    addSpace(after);
  };

  const numberedParagraph = (number, text, {
    size = BODY_SIZE,
    lineHeight = 18,
    after = 10,
  } = {}) => {
    const numberX = MARGIN + 18;
    const textX = MARGIN + 48;
    const lines = wrapText(regular, text, size, CONTENT_WIDTH - 48);
    lines.forEach((line, index) => {
      ensure(lineHeight);
      if (index === 0) {
        currentPage.drawText(`${number}.`, {
          x: numberX,
          y: cursorY,
          size,
          font: regular,
          color: BLACK,
        });
      }
      currentPage.drawText(line, {
        x: textX,
        y: cursorY,
        size,
        font: regular,
        color: BLACK,
      });
      cursorY -= lineHeight;
    });
    addSpace(after);
  };

  const centeredHeading = (text, {
    font = bold,
    size = 13,
    lineHeight = 18,
    after = 14,
    underline = false,
  } = {}) => {
    ensure(lineHeight + after);
    const cleaned = cleanText(text);
    const textWidth = font.widthOfTextAtSize(cleaned, size);
    const textX = (PAGE_WIDTH - textWidth) / 2;
    currentPage.drawText(cleaned, {
      x: textX,
      y: cursorY,
      size,
      font,
      color: BLACK,
    });
    if (underline) {
      currentPage.drawLine({
        start: { x: textX, y: cursorY - 2 },
        end: { x: textX + textWidth, y: cursorY - 2 },
        thickness: 0.7,
        color: BLACK,
      });
    }
    cursorY -= lineHeight;
    addSpace(after);
  };

  const table = (columns, rows, { size = 8.5, lineHeight = 10.5, after = 16 } = {}) => {
    const header = columns.map((column) => column.label);
    const headerHeight = tableRowHeight(header, columns, bold, size, lineHeight);
    const drawHeader = () => {
      ensure(headerHeight);
      cursorY = drawTableRow(currentPage, {
        row: header,
        columns,
        x: MARGIN,
        topY: cursorY,
        height: headerHeight,
        font: bold,
        size,
        lineHeight,
        fill: LIGHT_GRAY,
      });
    };

    drawHeader();
    rows.forEach((row) => {
      const height = tableRowHeight(row, columns, regular, size, lineHeight);
      if (cursorY - height < bottomY) {
        nextPage();
        drawHeader();
      }
      cursorY = drawTableRow(currentPage, {
        row,
        columns,
        x: MARGIN,
        topY: cursorY,
        height,
        font: regular,
        size,
        lineHeight,
      });
    });
    addSpace(after);
  };

  const consume = (height) => {
    cursorY -= height;
  };

  return {
    addSpace,
    centeredHeading,
    consume,
    ensure,
    getPage: () => currentPage,
    getY: () => cursorY,
    numberedParagraph,
    paragraph,
    table,
  };
}

function serviceMethodDisplay(method) {
  return String(method || "").toLowerCase() === "certified"
    ? "Certified Mail"
    : "United States first-class mail (USM)";
}

function recipientAddressLines(recipient) {
  const cityLine = safeLine(
    recipient?.city ? `${recipient.city},` : "",
    recipient?.state,
    recipient?.zipCode,
  );
  const structured = [recipient?.street1, recipient?.street2, cityLine]
    .map((line) => cleanText(line).trim())
    .filter(Boolean);
  if (structured.length) return structured;
  return cleanText(recipient?.addressLine || "").trim()
    ? [cleanText(recipient.addressLine).trim()]
    : [];
}

function resolvedFields({ caseData, creditor, property, attorney, details, today }) {
  const names = filingDebtorNames(caseData);
  const otherLiens = (details?.otherLiens || []).map((lien) => ({
    name: cleanText(lien?.name || "Other lienholder"),
    nature: cleanText(lien?.nature || "Lien"),
    date: cleanText(lien?.date || ""),
    amount: Math.max(0, numberValue(lien?.amount)),
  }));
  const otherLiensTotal = otherLiens.reduce((total, lien) => total + lien.amount, 0);
  const serviceRecipients = (details?.serviceRecipients || []).map((recipient) => ({
    creditorId: cleanText(recipient?.creditorId || ""),
    name: cleanText(recipient?.name || "Creditor"),
    street1: cleanText(recipient?.street1 || ""),
    street2: cleanText(recipient?.street2 || ""),
    city: cleanText(recipient?.city || ""),
    state: cleanText(recipient?.state || ""),
    zipCode: cleanText(recipient?.zipCode || ""),
    addressLine: cleanText(recipient?.addressLine || ""),
    method: cleanText(recipient?.method || "USM"),
  }));
  const calculation = calculateLienAvoidance({
    lienAmount: details?.lienAmount,
    otherLiensTotal,
    exemptionAmount: details?.exemptionAmount,
    propertyValue: details?.propertyValue,
  });

  if (!caseData) throw new Error("Case information is required.");
  if (!creditor) throw new Error("Select a judgment lien creditor.");
  if (!property) throw new Error("Select the property encumbered by the judgment lien.");
  if (!attorney) throw new Error("Select the attorney signing the motion.");
  if (!cleanText(details?.civilCaseNumber).trim()) throw new Error("Enter the civil case number for the judgment.");
  if (!cleanText(details?.book).trim()) throw new Error("Enter the judgment book number.");
  if (!cleanText(details?.page).trim()) throw new Error("Enter the judgment page number.");
  if (!cleanText(details?.county).trim()) throw new Error("Enter the county where the judgment lien is recorded.");
  if (!cleanText(details?.propertyDescription).trim()) throw new Error("Enter a property description.");
  if (!cleanText(details?.exemptionCode).trim()) throw new Error("Enter the statutory exemption used for the property.");
  if (calculation.lienAmount <= 0) throw new Error("Enter the amount of the judgment lien.");
  if (calculation.exemptionAmount <= 0) throw new Error("Enter the amount of the claimed exemption.");
  if (calculation.avoidableAmount <= 0) throw new Error("The current figures do not produce an avoidable lien amount.");
  if (!serviceRecipients.length) throw new Error("Select at least one creditor for service.");
  if (!serviceRecipients.some((recipient) => String(recipient.creditorId) === String(creditor.id))) {
    throw new Error("Choose USM or Certified Mail for the judgment lien creditor.");
  }
  serviceRecipients.forEach((recipient) => {
    if (!recipientAddressLines(recipient).length) {
      throw new Error(`Enter a mailing address for ${recipient.name}.`);
    }
  });

  return {
    names,
    caseNumber: caseData?.case_number || "",
    chapter: caseData?.chapter || "",
    creditorName: cleanText(creditor?.account_name || "Judgment lien creditor"),
    propertyDescription: cleanText(details.propertyDescription),
    exemptionCode: cleanText(details.exemptionCode),
    civilCaseNumber: cleanText(details.civilCaseNumber),
    lienDate: cleanText(details.lienDate || creditor?.date_opened || ""),
    book: cleanText(details.book),
    page: cleanText(details.page),
    recordingReference: recordingReference(details.book, details.page),
    county: cleanText(details.county),
    otherLiens,
    serviceRecipients,
    calculation,
    attorney,
    today: formatLongDate(today),
  };
}

function setDocumentMetadata(pdfDoc, title) {
  pdfDoc.setTitle(title);
  pdfDoc.setAuthor("Cisowski Law");
  pdfDoc.setCreator("Bankruptcy Manager");
  pdfDoc.setProducer("Bankruptcy Manager");
}

export async function buildLienAvoidanceMotionPdf({
  caseData,
  creditor,
  property,
  attorney,
  details,
  today = new Date(),
}) {
  const fields = resolvedFields({ caseData, creditor, property, attorney, details, today });
  const pdfDoc = await PDFDocument.create();
  const regular = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  setDocumentMetadata(pdfDoc, "Motion to Avoid Judicial Lien");

  const firstPage = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  drawCourtHeading(firstPage, attorney, bold, 733);
  drawCaption(firstPage, {
    names: fields.names,
    caseNumber: fields.caseNumber,
    chapter: fields.chapter,
    regular,
    bold,
    topY: 668,
  });
  drawCentered(firstPage, "Motion to Avoid Judicial Lien", bold, 13, 560);
  firstPage.drawLine({
    start: { x: 220, y: 557 },
    end: { x: 392, y: 557 },
    thickness: 0.7,
    color: BLACK,
  });

  const noticeBottom = drawNotice(firstPage, { attorney, regular, bold, topY: 536 });
  const flow = createFlowLayout({
    pdfDoc,
    regular,
    bold,
    page: firstPage,
    y: noticeBottom - 24,
    topY: PAGE_HEIGHT - MARGIN,
    bottomY: MARGIN,
  });
  flow.paragraph(
    "Comes now the debtor(s), by counsel, and request(s) an order avoiding the judicial lien described below pursuant to 11 U.S.C. § 522(f)(1)(A) and Federal Rule of Bankruptcy Procedure 4003(d). In support of this request, the debtor(s) state(s):",
    {
      size: BODY_SIZE,
      lineHeight: 18,
      firstLineIndent: 36,
      after: 10,
    },
  );

  flow.numberedParagraph(
    1,
    `The judicial lien is held by ${fields.creditorName} and is in the amount of ${formatLienCurrency(fields.calculation.lienAmount)}, a copy of which is attached. The lien does not secure a domestic support obligation as defined by 11 U.S.C. § 101(14A).`,
  );
  flow.numberedParagraph(
    2,
    `The judicial lien encumbers property identified as follows: ${fields.propertyDescription}.`,
  );
  flow.numberedParagraph(
    3,
    `The judicial lien is entered of record as Civil Case No. ${fields.civilCaseNumber}, judgment recorded as ${fields.recordingReference} in ${fields.county} County${fields.lienDate ? ` on ${formatLienDate(fields.lienDate)}` : ""}.`,
  );
  flow.numberedParagraph(
    4,
    `The property has been claimed as exempt under ${fields.exemptionCode} to the extent of ${formatLienCurrency(fields.calculation.exemptionAmount)}.`,
  );

  flow.numberedParagraph(
    5,
    `The value of the debtor(s)' interest in the property is ${formatLienCurrency(fields.calculation.propertyValue)}.`,
  );
  flow.numberedParagraph(
    6,
    "The property is encumbered by the following liens, excluding liens that have been avoided, in addition to the judicial lien described above:",
  );

  const otherLienRows = fields.otherLiens.length
    ? fields.otherLiens.map((lien) => [
      lien.name,
      lien.nature,
      formatLienDate(lien.date),
      formatLienCurrency(lien.amount),
    ])
    : [["None identified", "-", "-", formatLienCurrency(0)]];
  flow.table(
    [
      { label: "Lienholder", width: 138 },
      { label: "Nature of lien", width: 140 },
      { label: "Lien date", width: 90 },
      { label: "Amount of lien", width: 100 },
    ],
    otherLienRows,
    { size: 9.5, lineHeight: 11.5 },
  );

  flow.numberedParagraph(7, "Calculation of impairment under 11 U.S.C. § 522(f)(2)(A):");
  flow.paragraph(
    "Add (A), (B), and (C), then subtract (D) to determine (E), the extent of impairment. If (E) equals or exceeds (A), the entire judicial lien is avoidable. If (E) is less than (A), only the amount of (E) is avoidable and the difference between (A) and (E) remains secured.",
    { size: BODY_SIZE, lineHeight: 18, after: 10 },
  );
  flow.table(
    [
      { label: "Creditor", width: 102 },
      { label: "Lien (A)", width: 72 },
      { label: "Other liens (B)", width: 76 },
      { label: "Exemption (C)", width: 76 },
      { label: "Property value (D)", width: 72 },
      { label: "Impairment (E)", width: 70 },
    ],
    [[
      fields.creditorName,
      formatLienCurrency(fields.calculation.lienAmount),
      formatLienCurrency(fields.calculation.otherLiensTotal),
      formatLienCurrency(fields.calculation.exemptionAmount),
      formatLienCurrency(fields.calculation.propertyValue),
      formatLienCurrency(fields.calculation.impairment),
    ]],
    { size: 9.25, lineHeight: 11.25 },
  );

  flow.numberedParagraph(
    8,
    `The total of the judicial lien, all other unavoidable liens, and the exemption is ${formatLienCurrency(fields.calculation.totalLiensAndExemption)}. That amount exceeds the value of the debtor(s)' interest in the property by ${formatLienCurrency(fields.calculation.impairment)} (the "Impairment Amount").`,
  );

  const reliefText = fields.calculation.fullyAvoided
    ? `Accordingly, the judicial lien impairs the exemption and should be avoided in its entirety in the amount of ${formatLienCurrency(fields.calculation.avoidableAmount)}, because the Impairment Amount equals or exceeds the amount of the judicial lien.`
    : `Accordingly, the judicial lien impairs the exemption and should be avoided to the extent of ${formatLienCurrency(fields.calculation.avoidableAmount)}, leaving an unavoidable secured lien of ${formatLienCurrency(fields.calculation.remainingSecuredAmount)}.`;
  flow.numberedParagraph(9, reliefText);

  flow.ensure(118);
  let page = flow.getPage();
  let y = flow.getY();
  page.drawText(`Date: ${fields.today}`, { x: MARGIN, y, size: BODY_SIZE, font: regular, color: BLACK });
  drawSignatureBlock(page, { attorney, x: 330, y, regular, bold });
  flow.consume(118);
  flow.addSpace(22);

  flow.centeredHeading("Certificate of Service", {
    size: 13,
    lineHeight: 18,
    after: 12,
    underline: true,
  });
  flow.paragraph(
    `I hereby certify that on ${fields.today}, copies of the Motion to Avoid Judicial Lien and the proposed Order on Motion to Avoid Judicial Lien were served electronically on the Chapter 7 Trustee and the Attorney for the United States Trustee through the Court's electronic noticing, and by United States first-class mail (USM) or Certified Mail (Certified), as indicated below, on the creditors listed below:`,
    {
      size: BODY_SIZE,
      lineHeight: 18,
      firstLineIndent: 36,
      after: 14,
    },
  );

  fields.serviceRecipients.forEach((recipient, recipientIndex) => {
    const addressLines = recipientAddressLines(recipient);
    flow.ensure(48 + (addressLines.length * 15));
    flow.paragraph(recipient.name, {
      font: bold,
      size: 10.5,
      lineHeight: 15,
      after: 0,
    });
    addressLines.forEach((line) => {
      flow.paragraph(line, { size: 10.5, lineHeight: 15, after: 0 });
    });
    flow.paragraph(`Served by: ${serviceMethodDisplay(recipient.method)}`, {
      size: 10.5,
      lineHeight: 15,
      after: recipientIndex === fields.serviceRecipients.length - 1 ? 0 : 14,
    });
  });

  flow.ensure(100);
  page = flow.getPage();
  y = flow.getY();
  drawSignatureBlock(page, { attorney, x: 330, y, regular, bold });
  flow.consume(100);

  return pdfDoc.save();
}

export async function buildLienAvoidanceOrderPdf({
  caseData,
  creditor,
  property,
  attorney,
  details,
  today = new Date(),
}) {
  const fields = resolvedFields({ caseData, creditor, property, attorney, details, today });
  const pdfDoc = await PDFDocument.create();
  const regular = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  setDocumentMetadata(pdfDoc, "Order on Motion to Avoid Judicial Lien");

  const page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  // Preserve the order template's approximately four inches of clear space above the caption.
  drawCourtHeading(page, attorney, bold, 493);
  drawCaption(page, {
    names: fields.names,
    caseNumber: fields.caseNumber,
    chapter: fields.chapter,
    regular,
    bold,
    topY: 428,
  });
  drawCentered(page, "Order on Motion to Avoid Judicial Lien", bold, 13, 323);
  page.drawLine({
    start: { x: 190, y: 320 },
    end: { x: 422, y: 320 },
    thickness: 0.7,
    color: BLACK,
  });

  const orderRelief = fields.calculation.fullyAvoided
    ? `The lien is avoided in its entirety in the amount of ${formatLienCurrency(fields.calculation.avoidableAmount)}.`
    : `The lien is avoided to the extent of ${formatLienCurrency(fields.calculation.avoidableAmount)}, leaving an unavoidable secured lien of ${formatLienCurrency(fields.calculation.remainingSecuredAmount)}.`;
  const flow = createFlowLayout({
    pdfDoc,
    regular,
    bold,
    page,
    y: 285,
    topY: PAGE_HEIGHT - MARGIN,
    bottomY: MARGIN,
  });
  flow.paragraph(
    `This matter is before the Court on the debtor(s)' Motion to Avoid Judicial Lien under 11 U.S.C. § 522(f)(1)(A) and Federal Rule of Bankruptcy Procedure 4003(d), filed on ${fields.today}. No timely objection was filed. The motion is granted. The judicial lien of ${fields.creditorName}, entered as Civil Case No. ${fields.civilCaseNumber} and recorded as ${fields.recordingReference} in ${fields.county} County, impairs the debtor(s)' exemption claimed under ${fields.exemptionCode}. ${orderRelief}`,
    {
      size: BODY_SIZE,
      lineHeight: 26,
      firstLineIndent: 36,
      after: 12,
    },
  );

  flow.centeredHeading("# # #", {
    font: regular,
    size: BODY_SIZE,
    lineHeight: 18,
    after: 18,
  });
  flow.ensure(112);
  const signaturePage = flow.getPage();
  const signatureY = flow.getY();
  signaturePage.drawText("APPROVED FOR ENTRY BY:", {
    x: MARGIN,
    y: signatureY,
    size: BODY_SIZE,
    font: regular,
    color: BLACK,
  });
  drawSignatureBlock(signaturePage, {
    attorney,
    x: 330,
    y: signatureY,
    regular,
    bold,
  });
  flow.consume(112);

  return pdfDoc.save();
}

function safeFilenamePart(value) {
  return String(value || "case").replace(/[^a-zA-Z0-9_-]+/g, "_");
}

export async function prepareLienAvoidancePdfs({ caseData, creditor, property, attorney, details }) {
  const [motionBytes, orderBytes] = await Promise.all([
    buildLienAvoidanceMotionPdf({ caseData, creditor, property, attorney, details }),
    buildLienAvoidanceOrderPdf({ caseData, creditor, property, attorney, details }),
  ]);
  const identifier = safeFilenamePart(caseData?.case_number || caseData?.case_id);
  const creditorIdentifier = safeFilenamePart(creditor?.account_name || "Creditor");
  return {
    motion: {
      bytes: motionBytes,
      filename: `Motion_to_Avoid_Lien_${identifier}_${creditorIdentifier}.pdf`,
    },
    order: {
      bytes: orderBytes,
      filename: `Order_on_Motion_to_Avoid_Lien_${identifier}_${creditorIdentifier}.pdf`,
    },
  };
}

export function downloadLienAvoidancePdf({ bytes, filename, contentType = "application/pdf" }) {
  const blob = new Blob([bytes], { type: contentType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  requestAnimationFrame(() => {
    URL.revokeObjectURL(url);
    link.remove();
  });
}
